import type { ModelComparisonCompareProduct } from '@backmarket/http-api/src/api-specs-model-comparison/models/compare-models'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'

import type { RatingValue } from '../../models/productComparison'
import { getCategorySlug } from '../../utils/compareTracking'
import { useCompareTrackingContext } from '../useCompareTracking/useCompareTracking'
import type { BreadcrumbItem } from '../useEnrichResultsPage/useEnrichResultsPage.types'

type ProductClickParams = {
  product: ModelComparisonCompareProduct
  position: number
  listName: 'compare_hero' | 'compare_sticky_header'
  ctaLabel: 'see_options'
}

type ReviewsClickParams = {
  product: ModelComparisonCompareProduct
  rating: RatingValue
  position: number
}

type SwitcherClickParams = {
  previousProduct: ModelComparisonCompareProduct | undefined
  position: number
  newSlug: string
}

type TabClickParams = {
  tabId: string
  tabLabel: string
  position: number
}

type VariantClickParams = {
  label: string
  variantGroupId: string
  targetUrl: string
  position: number
}

// Single home for compare click tracking so Main and ResultsLayout build the
// same payloads. Reads the shared context populated by `useCompareTracking`.
export function useCompareClickTracking() {
  const tracking = useTracking()
  const compareContext = useCompareTrackingContext()

  function withCompareContext(payload: Record<string, unknown>) {
    return { ...(compareContext.value ?? {}), ...payload }
  }

  function trackProductClick({
    product,
    position,
    listName,
    ctaLabel,
  }: ProductClickParams) {
    tracking.trackCompareProductClicked(
      withCompareContext({
        list_name: listName,
        position,
        cta_label: ctaLabel,
        product_id: product.productId,
        product_model: product.title,
        product_category: compareContext.value?.compare_category,
        product_price: product.price?.amount,
        product_price_new: product.priceWhenNew?.amount,
      }),
    )
  }

  function trackReviewsClick({
    product,
    rating,
    position,
  }: ReviewsClickParams) {
    tracking.trackClick(
      withCompareContext({
        name: 'reviews_link',
        zone: 'compare_hero',
        position,
        product_id: product.productId,
        product_model: product.title,
        product_rating: rating.averageRate,
        product_nb_reviews: rating.count,
      }),
    )
  }

  function trackCategoryClick(category: BreadcrumbItem) {
    tracking.trackClick(
      withCompareContext({
        name: 'plp_link',
        zone: 'compare_hero_cta',
        target_url: category.url,
        target_category: getCategorySlug(category.url),
      }),
    )
  }

  function trackSwitcherClick({
    previousProduct,
    position,
    newSlug,
  }: SwitcherClickParams) {
    tracking.trackClick(
      withCompareContext({
        name: 'product_switcher',
        zone: 'compare_switcher',
        position,
        previous_product_slug: previousProduct?.slug,
        previous_product_id: previousProduct?.productId,
        new_product_slug: newSlug,
      }),
    )
  }

  function trackTabClick({ tabId, tabLabel, position }: TabClickParams) {
    tracking.trackClick(
      withCompareContext({
        name: 'compare_tab',
        zone: 'compare_tabs',
        position,
        tab_id: tabId,
        tab_label: tabLabel,
      }),
    )
  }

  function trackVariantClick({
    label,
    variantGroupId,
    targetUrl,
    position,
  }: VariantClickParams) {
    tracking.trackClick(
      withCompareContext({
        name: 'product_variant',
        zone: 'compare_variants',
        position,
        variant_label: label,
        variant_group_id: variantGroupId,
        target_url: targetUrl,
      }),
    )
  }

  return {
    trackProductClick,
    trackReviewsClick,
    trackCategoryClick,
    trackSwitcherClick,
    trackTabClick,
    trackVariantClick,
  }
}
